"use client";

/**
 * SignalRegistrationCard — renders the `application/vnd.xynth.signal-register+json`
 * cell output (emitted by the signals library on every run). Replaces the raw
 * text dump (`{'registered': 0, 'dry_run': True...}`) with a real card that
 * summarizes the run: what fired, what closed, the live book stats, and each
 * registered position's entry/exit rules + qualifying criteria.
 *
 * Payload shape (frozen by Agent A, signals/__init__.py _emit_register_display):
 *   { dry_run, registered, open_total, signal_name,
 *     preview: { all_time_pct, win_rate, total_trades, total_pnl_usd } | null,
 *     skipped: [{ index, reason }],
 *     closed_this_run: [{ name, exit_reason, pnl_pct, pnl_usd }],
 *     positions: [{ id, name, kind, ticker, direction, entry, confidence,
 *                  notional_usd, exit{take_profit,stop_loss,trail_pct,time_exit_days},
 *                  criteria{}, strategy_type, expiry, opened_at }] }
 */

import { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api-client";
import { EquityCurveChart } from "@/components/alert-page/signal-performance/EquityCurveChart";
import type {
  EquityPoint,
  PreviousTrade,
} from "@/lib/mock/signal-performance";
import {
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Target,
  ShieldAlert,
  Clock,
  LayoutDashboard,
  Activity,
} from "lucide-react";


interface SignalExit {
  take_profit?: number | null;
  stop_loss?: number | null;
  trail_pct?: number | null;
  time_exit_days?: number | null;
}

export interface SignalRegisterPosition {
  id?: string;
  name?: string;
  kind?: string;
  ticker?: string;
  direction?: string;
  entry?: number | null;
  confidence?: number | string | null;
  notional_usd?: number | null;
  exit?: SignalExit;
  criteria?: Record<string, string | number>;
  strategy_type?: string;
  expiry?: string | null;
  opened_at?: string | null;
}

export interface SignalRegisterData {
  dry_run?: boolean;
  /** Alert id of the signal — lets the hero node embed the live dashboard. */
  alert_id?: string;
  registered?: number;
  open_total?: number;
  signal_name?: string | null;
  preview?: {
    all_time_pct?: number | null;
    win_rate?: number | null;
    total_trades?: number | null;
    total_pnl_usd?: number | null;
  } | null;
  skipped?: Array<{ index?: string; reason?: string }>;
  closed_this_run?: Array<{
    name?: string;
    exit_reason?: string;
    pnl_pct?: number | null;
    pnl_usd?: number | null;
  }>;
  positions?: SignalRegisterPosition[];
}

function fmtPrice(v?: number | null): string {
  if (v == null || Number.isNaN(v)) return "—";
  return v >= 100 ? v.toFixed(2) : v.toFixed(v >= 1 ? 2 : 3);
}

function fmtUsd(v?: number | null): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `$${Math.round(v).toLocaleString()}`;
}

function fmtPct(v?: number | null): string {
  if (v == null || Number.isNaN(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(1)}%`;
}

function PositionRow({ p }: { p: SignalRegisterPosition }) {
  const short = (p.direction || "").toLowerCase() === "short";
  const exit = p.exit || {};
  return (
    <div className="rounded-sm border border-white/10 bg-black/40 px-3.5 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-white tracking-tight">
          {p.ticker || p.name || "—"}
        </span>
        <span
          className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
            short
              ? "border-red-500/30 bg-red-500/15 text-red-300"
              : "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
          }`}
        >
          {short ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
          {short ? "Short" : "Long"}
        </span>
        {p.strategy_type && (
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
            {p.strategy_type}
          </span>
        )}
        {p.notional_usd != null && (
          <span className="ml-auto text-[12px] font-bold text-white/80 tabular-nums">
            {fmtUsd(p.notional_usd)}
          </span>
        )}
      </div>

      {/* Entry + exit rules */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
        <span className="text-white/70">
          Entry <span className="font-bold text-white tabular-nums">${fmtPrice(p.entry)}</span>
        </span>
        {exit.take_profit != null && (
          <span className="inline-flex items-center gap-1 text-emerald-300">
            <Target className="w-3 h-3" />
            <span className="tabular-nums">${fmtPrice(exit.take_profit)}</span>
          </span>
        )}
        {exit.stop_loss != null && (
          <span className="inline-flex items-center gap-1 text-red-300">
            <ShieldAlert className="w-3 h-3" />
            <span className="tabular-nums">${fmtPrice(exit.stop_loss)}</span>
          </span>
        )}
        {exit.stop_loss == null && exit.trail_pct != null && (
          <span className="inline-flex items-center gap-1 text-red-300">
            <ShieldAlert className="w-3 h-3" />
            <span className="tabular-nums">{Math.round(exit.trail_pct * 100)}% trail</span>
          </span>
        )}
        {exit.time_exit_days != null && (
          <span className="inline-flex items-center gap-1 text-white/60">
            <Clock className="w-3 h-3" />
            <span className="tabular-nums">{exit.time_exit_days}d max</span>
          </span>
        )}
      </div>

      {/* Qualifying criteria chips */}
      {p.criteria && Object.keys(p.criteria).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Object.entries(p.criteria).map(([k, v]) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 rounded-sm border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[10.5px] text-white/70"
            >
              <span className="text-white/45">{k}</span>
              <span className="font-semibold text-white/90">{String(v)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Live dashboard embed for the hero node: equity curve + recent closed
 *  trades fetched from the public signal-performance endpoint. Fills the node
 *  with the real track record instead of dead space. */
function EmbeddedDashboard({ alertId }: { alertId: string }) {
  const [curve, setCurve] = useState<EquityPoint[]>([]);
  const [trades, setTrades] = useState<PreviousTrade[]>([]);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getSignalPerformance(alertId)
      .then((res: any) => {
        if (cancelled || !res) return;
        setCurve(Array.isArray(res.equity_curve) ? res.equity_curve : []);
        setTrades(
          Array.isArray(res.previous_trades) ? res.previous_trades.slice(0, 6) : [],
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [alertId]);

  if (curve.length === 0 && trades.length === 0) return null;

  return (
    <div className="px-4 py-3 border-t border-white/10 flex flex-col gap-3">
      {curve.length > 1 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1.5">
            Equity curve — $10k tracked book
          </p>
          <EquityCurveChart data={curve} height={170} />
        </div>
      )}
      {trades.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1.5">
            Recent closed trades
          </p>
          <div className="flex flex-col gap-1">
            {trades.map((t, i) => {
              const up = (t.pnl_pct ?? 0) >= 0;
              return (
                <div
                  key={t.id || `${t.ticker}-${i}`}
                  className="flex items-center gap-2 rounded-sm border border-white/10 bg-black/40 px-2.5 py-1.5 text-[12px]"
                >
                  <span className="font-bold text-white">{t.ticker}</span>
                  {t.strategy_type && (
                    <span className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
                      {t.strategy_type}
                    </span>
                  )}
                  <span className="text-white/45 tabular-nums">
                    {(t.closed_at || "").slice(0, 10)}
                  </span>
                  <span
                    className={`ml-auto font-bold tabular-nums ${up ? "text-emerald-300" : "text-red-300"}`}
                  >
                    {fmtPct(t.pnl_pct)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function SignalRegistrationCard({
  data,
  onViewDashboard,
  hero = false,
}: {
  data: SignalRegisterData;
  onViewDashboard?: () => void;
  /** Hero mode: fill the big "Final Results" canvas node edge-to-edge (no dead
   *  space), bigger stats band, and a full-width "View full dashboard" footer. */
  hero?: boolean;
}) {
  const positions = useMemo(
    () => (Array.isArray(data?.positions) ? data.positions : []),
    [data],
  );
  const closed = Array.isArray(data?.closed_this_run) ? data.closed_this_run : [];
  const registered = data?.registered ?? positions.length;
  const openTotal = data?.open_total ?? 0;
  const preview = data?.preview || null;

  return (
    <div
      className={`rounded-sm border border-emerald-500/25 bg-black/40 overflow-hidden ${
        hero ? "flex h-full flex-col" : ""
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/10">
        <div className="w-7 h-7 rounded-sm bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <Activity className="w-4 h-4 text-emerald-300" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-white tracking-tight truncate">
              {data?.signal_name || "Signal run"}
            </h3>
            {data?.dry_run && (
              <span className="shrink-0 rounded-sm border border-white/20 bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/70">
                Dry run
              </span>
            )}
          </div>
          <p className="text-[11.5px] text-white/60">
            {registered > 0
              ? `${registered} new position${registered === 1 ? "" : "s"}`
              : "No new trades this run"}
            {` · ${openTotal} open`}
            {closed.length > 0 ? ` · ${closed.length} closed` : ""}
          </p>
        </div>
        {onViewDashboard && (
          <button
            type="button"
            onClick={onViewDashboard}
            className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-1.5 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/25 transition-colors"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            View dashboard
          </button>
        )}
      </div>

      {/* Live book stats preview */}
      {preview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10">
          <Stat label="All-time" value={fmtPct(preview.all_time_pct)} tone={preview.all_time_pct} />
          <Stat
            label="Win rate"
            // Backend sends a 0–1 fraction (e.g. 0.732). Convert to percent;
            // tolerate already-percent values (>1) defensively.
            value={
              preview.win_rate != null
                ? `${Math.round(preview.win_rate <= 1 ? preview.win_rate * 100 : preview.win_rate)}%`
                : "—"
            }
          />
          <Stat label="Trades" value={preview.total_trades != null ? String(preview.total_trades) : "—"} />
          <Stat label="Total P&L" value={fmtUsd(preview.total_pnl_usd)} tone={preview.total_pnl_usd} />
        </div>
      )}

      {/* Registered positions */}
      {positions.length > 0 && (
        <div className={`p-3 flex flex-col gap-2 ${hero ? "flex-1 overflow-y-auto" : ""}`}>
          {positions.map((p, i) => (
            <PositionRow key={p.id || `${p.ticker}-${i}`} p={p} />
          ))}
        </div>
      )}

      {/* Closed this run */}
      {closed.length > 0 && (
        <div className="px-4 py-3 border-t border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">
            Closed this run
          </p>
          <div className="flex flex-col gap-1">
            {closed.map((c, i) => {
              const up = (c.pnl_pct ?? 0) >= 0;
              return (
                <div key={i} className="flex items-center gap-2 text-[12px]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-white/40 shrink-0" />
                  <span className="text-white font-medium">{c.name}</span>
                  <span className="text-white/50">{(c.exit_reason || "").replace(/_/g, " ")}</span>
                  <span
                    className={`ml-auto font-bold tabular-nums ${up ? "text-emerald-300" : "text-red-300"}`}
                  >
                    {fmtPct(c.pnl_pct)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hero node: fill with the live dashboard (equity curve + recents) */}
      {hero && data?.alert_id && <EmbeddedDashboard alertId={data.alert_id} />}

      {/* Empty state: no positions and nothing closed */}
      {positions.length === 0 && closed.length === 0 && (
        <div
          className={`px-4 text-[12.5px] text-white/60 ${
            hero && !data?.alert_id
              ? "flex flex-1 items-center justify-center text-center py-8"
              : "py-3"
          }`}
        >
          <span className={hero && !data?.alert_id ? "max-w-sm" : ""}>
            This run scanned the market and found no qualifying trades. The book is
            unchanged ({openTotal} open). The full track record and every open
            position live on the dashboard.
          </span>
        </div>
      )}

      {/* Hero footer — push to the bottom of the filled node and give a clear
          click-through to the real dashboard tab. */}
      {hero && onViewDashboard && (
        <div className="mt-auto border-t border-white/10 px-4 py-3">
          <button
            type="button"
            onClick={onViewDashboard}
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-emerald-500/40 bg-emerald-500/15 px-3 py-2.5 text-[12.5px] font-bold text-emerald-300 hover:bg-emerald-500/25 transition-colors"
          >
            <LayoutDashboard className="w-4 h-4" />
            View full dashboard
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: number | null;
}) {
  const color =
    tone == null ? "text-white" : tone >= 0 ? "text-emerald-300" : "text-red-300";
  return (
    <div className="bg-black/40 px-3 py-2 flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-widest text-white/55 font-bold">
        {label}
      </span>
      <span className={`text-sm font-bold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}
